import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const h = i % 12 || 12;
  const ampm = i < 12 ? 'AM' : 'PM';
  return { value: i, label: `${h}:00 ${ampm}` };
});

export default function SettingsPage() {
  const { profile, company, refreshProfile } = useAuth();
  const { showToast } = useToast();

  const [companyName,     setCompanyName]     = useState('');
  const [savingCompany,   setSavingCompany]   = useState(false);
  const [newPassword,     setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword,  setSavingPassword]  = useState(false);
  const [notifEmail,      setNotifEmail]      = useState('');
  const [eodEnabled,      setEodEnabled]      = useState(true);
  const [deadlineEnabled, setDeadlineEnabled] = useState(true);
  const [eodHour,         setEodHour]         = useState(18);
  const [savingNotif,     setSavingNotif]     = useState(false);
  const [notifLoaded,     setNotifLoaded]     = useState(false);

  useEffect(() => {
    setCompanyName(company?.name ?? '');
  }, [company?.name]);

  // Load notification preferences from profile
  useEffect(() => {
    if (!profile || notifLoaded) return;
    api.get<any>('/api/profile').then(data => {
      if (data) {
        setNotifEmail(data.notification_email ?? '');
        setEodEnabled(data.notif_eod_enabled ?? true);
        setDeadlineEnabled(data.notif_deadline_enabled ?? true);
        setEodHour(data.notif_eod_hour ?? 18);
      }
      setNotifLoaded(true);
    }).catch(() => setNotifLoaded(true));
  }, [profile, notifLoaded]);

  const saveCompanySettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (profile?.role !== 'admin' || !company?.id) return;
    setSavingCompany(true);
    try {
      await api.patch('/api/company', { name: companyName.trim() || 'My Company' });
      await refreshProfile();
      showToast('Company settings updated.', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to update company settings.', 'error');
    } finally {
      setSavingCompany(false);
    }
  };

  const savePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) { showToast('Password must be at least 6 characters.', 'error'); return; }
    if (newPassword !== confirmPassword) { showToast('Passwords do not match.', 'error'); return; }
    setSavingPassword(true);
    try {
      await api.patch('/api/auth/me/password', { newPassword });
      setNewPassword('');
      setConfirmPassword('');
      showToast('Password updated successfully.', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to update password.', 'error');
    } finally {
      setSavingPassword(false);
    }
  };

  const saveNotificationSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.user_id) return;
    if (notifEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifEmail)) {
      showToast('Please enter a valid notification email address.', 'error');
      return;
    }
    setSavingNotif(true);
    try {
      await api.patch('/api/profile', {
        notification_email:     notifEmail.trim() || null,
        notif_eod_enabled:      eodEnabled,
        notif_deadline_enabled: deadlineEnabled,
        notif_eod_hour:         eodHour,
      });
      showToast('Notification settings saved.', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to save notification settings.', 'error');
    } finally {
      setSavingNotif(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
      </div>

      {profile?.role === 'admin' && (
        <div className="form-card settings-card">
          <div className="settings-section-header">
            <div className="settings-section-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <h2 className="settings-section-title">Company Settings</h2>
          </div>
          <form onSubmit={saveCompanySettings}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Company Name</label>
                <input className="form-input" value={companyName} onChange={e => setCompanyName(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Invite Code</label>
                <input className="form-input" value={company?.invite_code ?? ''} disabled />
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" type="submit" disabled={savingCompany}>
                {savingCompany ? <><span className="btn-spinner" /> Saving…</> : 'Save Company Settings'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="form-card settings-card">
        <div className="settings-section-header">
          <div className="settings-section-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </div>
          <h2 className="settings-section-title">Security</h2>
        </div>
        <form onSubmit={savePassword}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-input" type="password" value={newPassword}
                onChange={e => setNewPassword(e.target.value)} placeholder="At least 6 characters" />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-input" type="password" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat password" />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-primary" type="submit" disabled={savingPassword}>
              {savingPassword ? <><span className="btn-spinner" /> Updating…</> : 'Update Password'}
            </button>
          </div>
        </form>
      </div>

      <div className="form-card settings-card">
        <div className="settings-section-header">
          <div className="settings-section-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
            </svg>
          </div>
          <div>
            <h2 className="settings-section-title">Notification Preferences</h2>
            <p className="settings-section-subtitle">Control when and where you receive task alerts</p>
          </div>
        </div>

        <form onSubmit={saveNotificationSettings}>
          <div className="form-group">
            <label className="form-label">
              Notification Email
              <span className="form-label-hint">(leave blank to use your account email: {profile?.email})</span>
            </label>
            <input
              className="form-input"
              type="email"
              value={notifEmail}
              onChange={e => setNotifEmail(e.target.value)}
              placeholder={profile?.email ?? 'your@email.com'}
            />
            <p className="field-hint">All task notification emails will be sent to this address.</p>
          </div>

          <div className="settings-toggle-row">
            <div className="settings-toggle-info">
              <label className="settings-toggle-label" htmlFor="eod-toggle">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                End-of-day task summary
              </label>
              <p className="settings-toggle-desc">Receive an email with your unfinished tasks at end of day.</p>
            </div>
            <label className="toggle-switch">
              <input id="eod-toggle" type="checkbox" checked={eodEnabled} onChange={e => setEodEnabled(e.target.checked)} />
              <span className="toggle-slider" />
            </label>
          </div>

          {eodEnabled && (
            <div className="form-group settings-sub-field">
              <label className="form-label">Send daily summary at (UTC)</label>
              <select className="form-select" value={eodHour} onChange={e => setEodHour(Number(e.target.value))} style={{ maxWidth: 180 }}>
                {HOURS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
              </select>
              <p className="field-hint">Times are in UTC. Your local offset may differ.</p>
            </div>
          )}

          <div className="settings-toggle-row">
            <div className="settings-toggle-info">
              <label className="settings-toggle-label" htmlFor="deadline-toggle">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                Task deadline alerts
              </label>
              <p className="settings-toggle-desc">Get an email 30 minutes before any task's deadline.</p>
            </div>
            <label className="toggle-switch">
              <input id="deadline-toggle" type="checkbox" checked={deadlineEnabled} onChange={e => setDeadlineEnabled(e.target.checked)} />
              <span className="toggle-slider" />
            </label>
          </div>

          <div className="form-actions">
            <button className="btn btn-primary" type="submit" disabled={savingNotif || !notifLoaded}>
              {savingNotif ? <><span className="btn-spinner" /> Saving…</> : 'Save Notification Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
